import { ApiCollectionResponse } from 'app/api';
import { FILE_UPLOADS_URL } from 'app/api/file-upload';
import { prepareRequestData } from 'app/api/helpers/prepareRequestData';
import { prepareResponseData } from 'app/api/helpers/prepareResponseData';
import { getAccessToken } from 'app/services/jwtService';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { API_URL } from '../core/config/index';
import { getErrorMessagesAsArray } from './errorHandling';

export const client = (() => {
  const token = getAccessToken();

  let instance = axios.create({
    baseURL: API_URL + '/v2/',
    // timeout: 1000,
    headers: {
      Accept: '*/*',
      'Content-type': 'application/json; charset=UTF-8',
      common: {
        Authorization: 'Bearer ' + token,
      },
    },
  });

  instance = registerRequestInterceptor(instance);
  instance = registerResponseInterceptor(instance);

  return instance;
})();

export const request = async function (
  options: AxiosRequestConfig,
  parseFn?: (data: any) => void
) {
  const onSuccess = function (response: AxiosResponse) {
    const { data } = response;
    return parseFn ? parseFn(data) : data;
  };

  const onError = function (error: AxiosError) {
    return Promise.reject(getErrorMessagesAsArray(error));
  };

  // Add current JWT token to the request (for cases like login-as / logout and login as another user etc)
  const opts: AxiosRequestConfig = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: 'Bearer ' + getAccessToken(),
    },
  };

  return client(opts).then(onSuccess).catch(onError);
};

export const getCollectionResponse = function <T>(
  data: any
): ApiCollectionResponse<T> {
  return {
    results: data.models.results,
    totalEntries: data.models.total_entries,
    totalPages: data.models.total_pages,
    hasNextPage: data.models.has_next_page,
    hasPreviousPage: data.models.has_previous_page,
  };
};

function registerRequestInterceptor(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use(
    function (config) {
      return {
        ...config,
        data: prepareRequestData(config.data),
        params: prepareRequestData(config.params),
      };
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  return instance;
}

function registerResponseInterceptor(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.response.use(
    function (response) {
      // exeptions - presign url to AwsS3 upload
      if (response.config.url === FILE_UPLOADS_URL) {
        return response;
      }

      return { ...response, data: prepareResponseData(response.data) };
    },
    (err) => {
      return Promise.reject(err);
    }
  );
  return instance;
}
